import React, { useEffect, useRef, useState } from 'react'
import { Modal } from '@surecompanies/core_components'
import { CopyOutlined } from '@ant-design/icons'

import type { TransactionViewRow } from '../../api/transaction-details-api-client'
import {
  collectAllocationTickets,
  DASH,
  formatDate,
  formatMoney,
  formatMonth,
  insuredPersonName,
  reconStatusLabel,
  sourceLabel,
  summarizeAllocationField,
} from './helpers'
import styles from './TransactionDetailModal.module.scss'

/**
 * Row-click detail modal. Only real fields from TransactionViewRow /
 * TransactionViewAllocation are shown — no fabricated card tokens or mailing
 * addresses (unlike the design mock's per-channel "Check details" / "Virtual
 * card details" sections, which invented data we don't have). "Download proof
 * of payment" is omitted for v1; Close is the only action, plus the header's
 * built-in ✕.
 */

interface TransactionDetailModalProps {
  transaction: TransactionViewRow | null
  onClose: () => void
}

interface Field {
  label: string
  value: string
}

type CopiedField = 'number' | 'id'

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  transaction,
  onClose,
}) => {
  // Hooks run unconditionally, ahead of the `!transaction` early return below
  // (Rules of Hooks) — this component instance stays mounted across
  // open/close since the parent always renders it and toggles `transaction`
  // between null and a value, rather than mounting/unmounting the component.
  const [copied, setCopied] = useState<CopiedField | null>(null)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset the "Copied" state whenever the displayed transaction changes (new
  // row opened, or this one closed) so a stale label can't linger across
  // transactions.
  useEffect(() => {
    setCopied(null)
  }, [transaction?.transactionId])

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    },
    []
  )

  if (!transaction) return null

  const handleCopy = async (field: CopiedField, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(field)
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
      copyTimeoutRef.current = setTimeout(() => setCopied(null), 1600)
    } catch {
      // Clipboard API unavailable or permission denied — no destructive
      // fallback needed, the value is already visible in the header.
    }
  }

  const { transactionNumber, transactionId } = transaction
  const titleField: CopiedField = transactionNumber ? 'number' : 'id'

  const { allocations } = transaction
  const isSplit = allocations.length > 1
  const primaryAllocation = allocations[0] ?? null

  // Unallocated transactions can still have a knowable benefit — fall back to it
  // for the benefit fields so the detail matches the row. Coverage month stays a
  // dash: there is no allocated period yet.
  const resolved = allocations.length === 0 ? transaction.resolvedBenefit : null

  const company = allocations.length
    ? summarizeAllocationField(allocations, (a) => a.companyName)
    : (resolved?.companyName ?? DASH)
  const month = summarizeAllocationField(allocations, (a) =>
    formatMonth(a.periodStartDate)
  )

  // Benefit id(s) come from allocations — a split can span several, so show the
  // single full id when they agree, a count when they differ. With no
  // allocation, fall back to the resolved benefit id, else a dash.
  const benefitIds = Array.from(
    new Set(allocations.map((a) => a.benefitId).filter(Boolean))
  )
  const benefitIdValue =
    benefitIds.length === 0
      ? (resolved?.benefitId ?? DASH)
      : benefitIds.length === 1
        ? benefitIds[0]
        : `${benefitIds.length} benefits`

  // Same idea for insured person: count unique people, not allocations — a
  // single member split across benefits/months is still one person. With no
  // allocation, fall back to the resolved benefit's insured person.
  const insuredNames = Array.from(
    new Set(allocations.map(insuredPersonName).filter((name) => name !== DASH))
  )
  const insuredPersonValue =
    insuredNames.length === 0
      ? resolved
        ? insuredPersonName(resolved)
        : DASH
      : insuredNames.length === 1
        ? insuredNames[0]
        : `${insuredNames.length} people`

  const fields: Field[] = [
    {
      label: 'Carrier',
      value: allocations.length
        ? summarizeAllocationField(allocations, (a) => a.carrierName)
        : (resolved?.carrierName ?? DASH),
    },
    { label: 'Payment type', value: sourceLabel(transaction.source) },
    { label: 'Company', value: company },
    {
      label: 'Company EE ID',
      value: isSplit
        ? summarizeAllocationField(allocations, (a) => a.employeePublicKey)
        : (primaryAllocation?.employeePublicKey ??
          resolved?.employeePublicKey ??
          DASH),
    },
    {
      label: 'Insured person',
      value: insuredPersonValue,
    },
    {
      label: 'Benefit ID',
      value: benefitIdValue,
    },
    { label: 'Coverage month', value: month },
    {
      label: 'Transaction date',
      value: formatDate(transaction.transactionDate),
    },
    { label: 'Amount', value: formatMoney(transaction.amount) },
    { label: 'Reconciliation', value: reconStatusLabel(transaction) },
  ]

  if (transaction.txReturn) {
    fields.push(
      { label: 'Return note', value: transaction.txReturn.note },
      {
        label: 'Returned by',
        value: `${transaction.txReturn.returnedBy} · ${formatDate(transaction.txReturn.returnedAt)}`,
      }
    )
  }
  if (transaction.checkNumber) {
    fields.push({ label: 'Check number', value: transaction.checkNumber })
  }
  if (transaction.token) {
    fields.push({ label: 'Token', value: transaction.token })
  }
  if (isSplit) {
    const allocatedTotal = allocations.reduce(
      (sum, a) => sum + (Number(a.amount) || 0),
      0
    )
    fields.push({
      label: 'Allocation summary',
      value: `Split × ${allocations.length} · allocated ${formatMoney(allocatedTotal)}`,
    })
  }

  const note = summarizeAllocationField(allocations, (a) => a.note)
  if (note !== DASH) {
    fields.push({ label: 'Note', value: note })
  }

  const tickets = collectAllocationTickets(allocations)

  return (
    <Modal
      open
      onCancel={onClose}
      centered
      width={560}
      destroyOnClose
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
      title={
        <div className={styles.header}>
          <span className={styles.kicker}>
            {sourceLabel(transaction.source)} transaction
          </span>
          {/* The transaction number is the title when there is one; the UUID
              then drops to a line beneath it. An unnumbered transaction
              (EE-paid) keeps the UUID as its title. */}
          <div className={styles.titleRow}>
            <h2 className={styles.title}>
              {transactionNumber || transactionId || DASH}
            </h2>
            {transactionNumber ? (
              <button
                type="button"
                className={styles.copyBtn}
                title="Copy transaction number"
                aria-label="Copy transaction number"
                onClick={() => void handleCopy('number', transactionNumber)}
              >
                <CopyOutlined />
              </button>
            ) : (
              transactionId && (
                <button
                  type="button"
                  className={styles.copyBtn}
                  title="Copy transaction ID"
                  aria-label="Copy transaction ID"
                  onClick={() => void handleCopy('id', transactionId)}
                >
                  <CopyOutlined />
                </button>
              )
            )}
            {copied === titleField && (
              <span className={styles.copiedLabel}>Copied</span>
            )}
          </div>
          {transactionNumber && transactionId && (
            <div className={styles.idRow}>
              <span className={styles.idValue}>{transactionId}</span>
              <button
                type="button"
                className={`${styles.copyBtn} ${styles.copyBtnSmall}`}
                title="Copy transaction ID"
                aria-label="Copy transaction ID"
                onClick={() => void handleCopy('id', transactionId)}
              >
                <CopyOutlined />
              </button>
              {copied === 'id' && (
                <span className={styles.copiedLabel}>Copied</span>
              )}
            </div>
          )}
          <span className={styles.subtitle}>
            {`${company} · ${month} · ${formatMoney(transaction.amount)}`}
          </span>
        </div>
      }
      footer={
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          Close
        </button>
      }
    >
      <div className={styles.grid}>
        {fields.map((field) => (
          <div key={field.label} className={styles.field}>
            <span className={styles.fieldLabel}>{field.label}</span>
            <span className={styles.fieldValue}>{field.value}</span>
          </div>
        ))}
      </div>

      {tickets.length > 0 && (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Tickets</span>
          <div className={styles.ticketList}>
            {tickets.map((ticket) => (
              <a
                key={ticket.ticketUrl}
                href={ticket.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.ticketLink}
              >
                #{ticket.ticketId ?? 'ticket'}
              </a>
            ))}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default TransactionDetailModal
